import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Mic, MicOff, Loader2, Square, RotateCcw, Sparkles, Crown } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { todayISO } from "@/lib/format";
import { maybeAlertBudgetForExpense } from "@/lib/budgetAlerts";

const STATE = {
  IDLE: "idle",
  RECORDING: "recording",
  PROCESSING: "processing",
  REVIEW: "review",
  SAVING: "saving",
};

function formatTimer(secs) {
  const m = Math.floor(secs / 60).toString().padStart(2, "0");
  const s = (secs % 60).toString().padStart(2, "0");
  return `${m}:${s}`;
}

export default function VoiceEntryDialog({
  open,
  onOpenChange,
  categories,
  onCreated,
  onLimitReached,
}) {
  const [state, setState] = useState(STATE.IDLE);
  const [usage, setUsage] = useState(null); // {used_today, limit, is_premium, remaining}
  const [elapsed, setElapsed] = useState(0);
  const [transcript, setTranscript] = useState("");
  const [type, setType] = useState("expense");
  const [category, setCategory] = useState("");
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(todayISO());
  const [note, setNote] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState("");

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);

  const filteredCategories = (categories || []).filter((c) => c.type === type);

  const cleanupStream = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    mediaRecorderRef.current = null;
    chunksRef.current = [];
  }, []);

  const resetAll = useCallback(() => {
    cleanupStream();
    setState(STATE.IDLE);
    setElapsed(0);
    setTranscript("");
    setType("expense");
    setCategory("");
    setAmount("");
    setDate(todayISO());
    setNote("");
  }, [cleanupStream]);

  // Load usage when dialog opens
  useEffect(() => {
    if (open) {
      api
        .get("/transactions/voice/usage")
        .then((r) => setUsage(r.data))
        .catch(() => setUsage(null));
      api
        .get("/accounts")
        .then((r) => {
          const list = r.data || [];
          setAccounts(list);
          if (list.length > 0 && !list.find((a) => a.id === accountId)) {
            const def = list.find((a) => a.is_default) || list[0];
            setAccountId(def.id);
          }
        })
        .catch(() => setAccounts([]));
    } else {
      resetAll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Ensure picked category stays valid when type changes
  useEffect(() => {
    if (state !== STATE.REVIEW) return;
    if (filteredCategories.length > 0 && !filteredCategories.find((c) => c.name === category)) {
      setCategory(filteredCategories[0].name);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, state]);

  const startRecording = async () => {
    if (!navigator.mediaDevices || !window.MediaRecorder) {
      toast.error("Tu navegador no soporta grabación de audio");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimeCandidates = [
        "audio/webm;codecs=opus",
        "audio/webm",
        "audio/ogg;codecs=opus",
        "audio/mp4",
      ];
      const mimeType = mimeCandidates.find((m) => window.MediaRecorder.isTypeSupported(m)) || "";
      const mr = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      chunksRef.current = [];
      mr.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      mr.onstop = async () => {
        const blobType = mr.mimeType || "audio/webm";
        const blob = new Blob(chunksRef.current, { type: blobType });
        cleanupStream();
        await uploadAudio(blob, blobType);
      };
      mediaRecorderRef.current = mr;
      mr.start();
      setState(STATE.RECORDING);
      setElapsed(0);
      timerRef.current = setInterval(() => {
        setElapsed((s) => {
          if (s >= 59) {
            // auto-stop at 60s
            try {
              mr.stop();
            } catch (_) {}
            return s;
          }
          return s + 1;
        });
      }, 1000);
    } catch (err) {
      console.error(err);
      toast.error("No se pudo acceder al micrófono. Revisa los permisos.");
      cleanupStream();
      setState(STATE.IDLE);
    }
  };

  const stopRecording = () => {
    const mr = mediaRecorderRef.current;
    if (mr && mr.state !== "inactive") {
      try {
        mr.stop();
      } catch (_) {}
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setState(STATE.PROCESSING);
  };

  const uploadAudio = async (blob, blobType) => {
    setState(STATE.PROCESSING);
    try {
      const ext = blobType.includes("mp4")
        ? "mp4"
        : blobType.includes("ogg")
        ? "ogg"
        : "webm";
      const fd = new FormData();
      fd.append("audio", blob, `voice.${ext}`);
      const res = await api.post("/transactions/voice", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const { transcript: tx, draft, usage: u } = res.data || {};
      if (u) setUsage((prev) => ({ ...(prev || {}), ...u, used_today: u.used_today }));
      setTranscript(tx || "");
      setType(draft?.type === "income" ? "income" : "expense");
      setCategory(draft?.category || "");
      setAmount(draft?.amount ? String(draft.amount) : "");
      setDate(draft?.date || todayISO());
      setNote(draft?.note || "");
      setState(STATE.REVIEW);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail;
      if (status === 403 && (detail?.code === "voice_limit_reached" || typeof detail === "object")) {
        toast.error("Has alcanzado tu límite diario gratis (5/día)");
        onOpenChange?.(false);
        onLimitReached?.();
        return;
      }
      toast.error(formatApiErrorDetail(detail) || "No se pudo procesar el audio");
      setState(STATE.IDLE);
    }
  };

  const handleRetry = () => {
    setTranscript("");
    setState(STATE.IDLE);
  };

  const handleSave = async (e) => {
    e?.preventDefault?.();
    if (!category) {
      toast.error("Selecciona una categoría");
      return;
    }
    if (!accountId) {
      toast.error("Selecciona una cuenta");
      return;
    }
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) {
      toast.error("El monto debe ser mayor a 0");
      return;
    }
    setState(STATE.SAVING);
    try {
      await api.post("/transactions", { type, category, amount: amt, date, note, source: "voice", account_id: accountId });
      toast.success(type === "income" ? "Ingreso registrado por voz" : "Gasto registrado por voz");
      if (type === "expense") {
        maybeAlertBudgetForExpense({ category, date });
      }
      onOpenChange?.(false);
      onCreated?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error al guardar");
      setState(STATE.REVIEW);
    }
  };

  const isFree = usage && !usage.is_premium;
  const remaining = usage?.remaining;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#131C2E] max-w-md"
        data-testid="voice-entry-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#F1F5F9] flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#0052FF]" />
            Registrar por voz
          </DialogTitle>
          <DialogDescription className="text-sm text-[#64748B]">
            Graba tu transacción y revísala antes de guardar.
          </DialogDescription>
        </DialogHeader>

        {/* Usage counter for free users */}
        {isFree && (
          <div
            className="flex items-center justify-between rounded-md border border-[#2A3447] bg-[#131C2E] px-3 py-2 text-xs text-[#475569]"
            data-testid="voice-usage-banner"
          >
            <span>
              Usos hoy:{" "}
              <strong className="text-[#F1F5F9] font-numeric">
                {usage.used_today}/{usage.limit}
              </strong>
            </span>
            <span className="flex items-center gap-1 text-[#B45309]">
              <Crown className="w-3.5 h-3.5" /> Suscríbete: ilimitado
            </span>
          </div>
        )}

        {/* IDLE — show record button */}
        {state === STATE.IDLE && (
          <div className="py-6 flex flex-col items-center gap-4" data-testid="voice-idle">
            <p className="text-sm text-center text-[#64748B] max-w-xs">
              Toca el micrófono y di algo como{" "}
              <em className="text-[#F1F5F9]">
                "Gasté 250 pesos en gasolina hoy"
              </em>
              .
            </p>
            <Button
              type="button"
              onClick={startRecording}
              disabled={isFree && remaining === 0}
              className="bg-[#0052FF] hover:bg-[#0040CC] text-white rounded-full h-20 w-20 p-0 shadow-lg"
              data-testid="voice-start-recording"
            >
              <Mic className="w-8 h-8" />
            </Button>
            {isFree && remaining === 0 && (
              <p className="text-xs text-[#EF4444]" data-testid="voice-no-remaining">
                Sin usos disponibles hoy.
              </p>
            )}
          </div>
        )}

        {/* RECORDING */}
        {state === STATE.RECORDING && (
          <div className="py-6 flex flex-col items-center gap-4" data-testid="voice-recording">
            <div className="flex items-center gap-2 text-[#EF4444]">
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#EF4444] opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-[#EF4444]"></span>
              </span>
              <span className="text-sm font-medium">Grabando…</span>
            </div>
            <div
              className="text-4xl font-numeric text-[#F1F5F9] tabular-nums"
              data-testid="voice-timer"
            >
              {formatTimer(elapsed)}
            </div>
            <Button
              type="button"
              onClick={stopRecording}
              className="bg-[#EF4444] hover:bg-[#DC2626] text-white rounded-full h-16 w-16 p-0"
              data-testid="voice-stop-recording"
            >
              <Square className="w-6 h-6 fill-white" />
            </Button>
            <p className="text-xs text-[#94A3B8]">Máx 60s · Toca para detener</p>
          </div>
        )}

        {/* PROCESSING */}
        {state === STATE.PROCESSING && (
          <div className="py-10 flex flex-col items-center gap-3" data-testid="voice-processing">
            <Loader2 className="w-8 h-8 animate-spin text-[#0052FF]" />
            <p className="text-sm text-[#64748B]">Transcribiendo e interpretando…</p>
          </div>
        )}

        {/* REVIEW + SAVING */}
        {(state === STATE.REVIEW || state === STATE.SAVING) && (
          <form onSubmit={handleSave} className="space-y-4 mt-1" data-testid="voice-review">
            {transcript && (
              <div className="rounded-md bg-[#1B2638] border border-[#2A3447] px-3 py-2">
                <Label className="text-[10px] uppercase tracking-[0.2em] text-[#94A3B8]">
                  Transcripción
                </Label>
                <p
                  className="text-sm text-[#F1F5F9] mt-1 italic"
                  data-testid="voice-transcript"
                >
                  "{transcript}"
                </p>
              </div>
            )}

            <Tabs value={type} onValueChange={setType}>
              <TabsList className="grid grid-cols-2 w-full bg-[#1B2638]">
                <TabsTrigger
                  value="expense"
                  className="data-[state=active]:bg-[#EF4444] data-[state=active]:text-white"
                  data-testid="voice-tab-expense"
                >
                  Gasto
                </TabsTrigger>
                <TabsTrigger
                  value="income"
                  className="data-[state=active]:bg-[#0052FF] data-[state=active]:text-white"
                  data-testid="voice-tab-income"
                >
                  Ingreso
                </TabsTrigger>
              </TabsList>
            </Tabs>

            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-[#94A3B8]">Cuenta</Label>
              <Select value={accountId} onValueChange={setAccountId}>
                <SelectTrigger
                  className="mt-2 h-11 bg-[#131C2E] border-[#2A3447]"
                  data-testid="voice-account-select"
                >
                  <SelectValue placeholder="Selecciona cuenta" />
                </SelectTrigger>
                <SelectContent className="bg-[#131C2E] max-h-64">
                  {accounts.map((a) => (
                    <SelectItem key={a.id} value={a.id}>
                      <span className="flex items-center gap-2">
                        <span
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: a.color }}
                        />
                        {a.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-[#94A3B8]">
                Categoría
              </Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger
                  className="mt-2 h-11 bg-[#131C2E] border-[#2A3447]"
                  data-testid="voice-category-select"
                >
                  <SelectValue placeholder="Selecciona categoría" />
                </SelectTrigger>
                <SelectContent className="bg-[#131C2E] max-h-64">
                  {filteredCategories.map((c) => (
                    <SelectItem key={c.id} value={c.name}>
                      <span className="flex items-center gap-2">
                        <span
                          className="w-2 h-2 rounded-full"
                          style={{ backgroundColor: c.color }}
                        />
                        {c.name}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-[#94A3B8]">
                  Monto
                </Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  className="mt-2 h-11 bg-[#131C2E] border-[#2A3447] font-numeric text-lg"
                  required
                  data-testid="voice-amount-input"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-[#94A3B8]">
                  Fecha
                </Label>
                <Input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="mt-2 h-11 bg-[#131C2E] border-[#2A3447]"
                  required
                  data-testid="voice-date-input"
                />
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-[0.2em] text-[#94A3B8]">
                Nota
              </Label>
              <Textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Notas o referencia..."
                className="mt-2 bg-[#131C2E] border-[#2A3447] resize-none"
                rows={2}
                data-testid="voice-note-input"
              />
            </div>

            <DialogFooter className="gap-2 flex-row sm:justify-between">
              <Button
                type="button"
                variant="outline"
                onClick={handleRetry}
                className="border-[#2A3447]"
                disabled={state === STATE.SAVING}
                data-testid="voice-retry-button"
              >
                <RotateCcw className="w-4 h-4 mr-1" /> Repetir
              </Button>
              <Button
                type="submit"
                disabled={state === STATE.SAVING}
                className="bg-[#0052FF] hover:bg-[#0040CC] text-white"
                data-testid="voice-save-button"
              >
                {state === STATE.SAVING ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  "Guardar"
                )}
              </Button>
            </DialogFooter>
          </form>
        )}

        {/* Fallback cancel button when not in review */}
        {(state === STATE.IDLE || state === STATE.RECORDING) && (
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange?.(false)}
              className="border-[#2A3447]"
              data-testid="voice-close-button"
            >
              <MicOff className="w-4 h-4 mr-1" /> Cerrar
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
